import {
  ReplayFromHistoryToolInputSchema,
  REPLAY_REQUEST_TOOL_NAME,
} from '@terra/shared-dto-chat-types';
import { executePortalReplayRequest } from '../../../../utils/replayRequestSession.utils';
import {
  buildReplayInputFromHistoryRow,
  loadHistoryRowForReplay,
} from '../../../../utils/replayFromHistory.utils';
import { ensureHeistSidCookieOnReplayHeaders } from '../../../../utils/booth-heist-sid-cookie.utils';
import {
  getElectronApiUnavailableResult,
  isElectronApiAvailable,
  parsePortalToolHandlerInput,
  resolvePortalApplicationId,
  resolvePortalTargetScope,
} from '../portal-tool-handler.utils';
import type { PortalToolHandler } from '../types';

function buildReplayHandlerResult({
  sessionId,
  response,
  sourceRequestId,
}: Awaited<ReturnType<typeof executePortalReplayRequest>> & {
  sourceRequestId?: string;
}) {
  if (response.error || response.statusCode === 0) {
    return {
      status: 'error' as const,
      result: {
        message: response.error || 'Replay request failed',
        navigation: {
          tabType: 'replay' as const,
          sessionId,
          label: 'Open in Replay',
        },
        data: {
          statusCode: response.statusCode,
          statusMessage: response.statusMessage,
          headers: response.headers,
          body: response.body,
          durationMs: response.duration,
          sessionId,
          error: response.error,
          sourceRequestId,
        },
      },
    };
  }

  return {
    status: 'success' as const,
    result: {
      message: `Replay completed with status ${response.statusCode}`,
      navigation: {
        tabType: 'replay' as const,
        sessionId,
        label: 'Open in Replay',
      },
      data: {
        statusCode: response.statusCode,
        statusMessage: response.statusMessage,
        headers: response.headers,
        body: response.body,
        durationMs: response.duration,
        sessionId,
        sourceRequestId,
      },
    },
  };
}

export const replayRequestHandler: PortalToolHandler = async ({
  input,
  toolCallId,
  batchId,
  applicationId,
  sessionId,
}) => {
  const parsedInput = parsePortalToolHandlerInput({
    schema: ReplayFromHistoryToolInputSchema,
    input,
    toolName: REPLAY_REQUEST_TOOL_NAME,
    toolCallId,
    batchId,
  });

  if (!parsedInput.ok) {
    return parsedInput.result;
  }

  if (!isElectronApiAvailable()) {
    return getElectronApiUnavailableResult({
      toolName: REPLAY_REQUEST_TOOL_NAME,
      toolCallId,
      batchId,
    });
  }

  const { targetId, targetType } = resolvePortalTargetScope({ applicationId });
  const resolvedApplicationId = resolvePortalApplicationId({ applicationId });
  const requestId = parsedInput.data.requestId;

  const historyResult = await loadHistoryRowForReplay({
    targetId,
    targetType,
    requestId,
  });

  if (!historyResult.success) {
    return {
      status: 'error',
      result: { message: historyResult.error },
    };
  }

  const replayInput = buildReplayInputFromHistoryRow({
    row: historyResult.row,
    requestBody: historyResult.requestBody,
    followRedirects: parsedInput.data.followRedirects,
    protocol: parsedInput.data.protocol,
  });

  const headers = await ensureHeistSidCookieOnReplayHeaders({
    headers: replayInput.headers ?? {},
    url: replayInput.url ?? historyResult.row.url,
    targetId,
    targetType,
  });

  const replayResult = await executePortalReplayRequest({
    input: {
      ...replayInput,
      headers,
    },
    applicationId: resolvedApplicationId,
    threadId: sessionId,
  });

  return buildReplayHandlerResult({
    ...replayResult,
    sourceRequestId: requestId,
  });
};
